import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import uuid from "react-uuid";
import { getFirestore, doc, setDoc } from "firebase/firestore";
import { app } from "../firebase";
import { DashboardSidebar } from "./Header";
import "./CreateForm.css";

import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import CreateIcon from "@mui/icons-material/Create";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

function CreateForm({ photoURL, value }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // Popup is CLOSED by default.
  // It opens only when the user clicks "Upload File".
  const [showInstructionPopup, setShowInstructionPopup] = useState(false);

  /* ================================
     CREATE FORM MANUALLY
  ================================= */

  const handleCreateManually = () => {
    const id = uuid();
    navigate("/form/" + id);
  };

  /* ================================
     DOWNLOAD EXCEL TEMPLATE
  ================================= */

  const downloadTemplate = () => {
    // Ensure XLSX library is loaded
    if (!window.XLSX) {
      const script = document.createElement("script");

      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";

      script.onload = () => {
        performTemplateDownload();
      };

      script.onerror = () => {
        setSnack({
          open: true,
          msg: "Failed to load Excel library. Please check your internet connection.",
          severity: "error",
        });
      };

      document.head.appendChild(script);
      return;
    }

    performTemplateDownload();
  };

  const performTemplateDownload = () => {
    if (!window.XLSX) {
      setSnack({
        open: true,
        msg: "Excel library is still loading. Please try again in a moment.",
        severity: "error",
      });
      return;
    }

    // Sample template data
    const templateData = [
      {
        QuestionText: "What is 2 + 2?",
        QuestionType: "radio",
        Option1: "3",
        Option2: "4",
        Option3: "5",
        Option4: "6",
        CorrectAnswer: "4",
        Points: 5,
        Required: "Yes",
      },
      {
        QuestionText:
          "Which of the following are fruits? (Select all that apply)",
        QuestionType: "checkbox",
        Option1: "Apple",
        Option2: "Carrot",
        Option3: "Banana",
        Option4: "Potato",
        CorrectAnswer: "Apple; Banana",
        Points: 10,
        Required: "Yes",
      },
      {
        QuestionText: "What is your favorite color?",
        QuestionType: "text",
        CorrectAnswer: "",
        Points: 0,
        Required: "No",
      },
    ];

    // Create worksheet
    const worksheet =
      window.XLSX.utils.json_to_sheet(templateData);

    // Column widths
    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 20 },
      { wch: 10 },
      { wch: 10 },
    ];

    // Create workbook
    const workbook = window.XLSX.utils.book_new();

    window.XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Questions"
    );

    // Download
    window.XLSX.writeFile(
      workbook,
      "Form_Template.xlsx"
    );

    setSnack({
      open: true,
      msg: "Template downloaded successfully!",
      severity: "success",
    });
  };

  /* ================================
     EXCEL UPLOAD
  ================================= */

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (!window.XLSX) {
      setSnack({
        open: true,
        msg: "Excel parser library is still loading. Please try again in a moment.",
        severity: "error",
      });
      return;
    }

    const reader = new FileReader();

    reader.onload = async (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);

        const workbook = window.XLSX.read(data, {
          type: "array",
        });

        const firstSheetName =
          workbook.SheetNames[0];

        const worksheet =
          workbook.Sheets[firstSheetName];

        const jsonData =
          window.XLSX.utils.sheet_to_json(
            worksheet,
            {
              defval: "",
            }
          );

        if (jsonData.length === 0) {
          setSnack({
            open: true,
            msg: "The uploaded Excel sheet is empty.",
            severity: "error",
          });
          return;
        }

        /* ================================
           PARSE QUESTIONS
        ================================= */

        const parsedQuestions = jsonData.map((row) => {
          const questionText =
            row["QuestionText"] ||
            row["Question"] ||
            row["question"] ||
            "Untitled Question";

          let questionType = String(
            row["QuestionType"] ||
              row["Type"] ||
              row["questionType"] ||
              row["type"] ||
              "radio"
          )
            .toLowerCase()
            .trim();

          if (questionType === "checkboxes") {
            questionType = "checkbox";
          }

          if (
            questionType === "textbox" ||
            questionType === "text box"
          ) {
            questionType = "text";
          }

          if (
            !["radio", "checkbox", "text"].includes(
              questionType
            )
          ) {
            questionType = "radio";
          }

          /* ================================
             OPTIONS
          ================================= */

          const options = [];

          for (let o = 1; o <= 5; o++) {
            const optionValue =
              row[`Option${o}`] ||
              row[`Option ${o}`] ||
              row[`option${o}`] ||
              row[`option ${o}`];

            if (
              optionValue !== undefined &&
              optionValue !== ""
            ) {
              options.push({
                optionText: String(
                  optionValue
                ).trim(),
              });
            }
          }

          if (
            options.length === 0 &&
            questionType !== "text"
          ) {
            options.push({
              optionText: "Option 1",
            });
          }

          /* ================================
             CORRECT ANSWER
          ================================= */

          const correctAnswerRaw =
            row["CorrectAnswer"] ||
            row["Correct Answer"] ||
            row["Answer"] ||
            row["answer"] ||
            "";

          let answerKey = "";

          if (questionType === "checkbox") {
            if (correctAnswerRaw) {
              answerKey = String(correctAnswerRaw)
                .split(/[;,|]+/)
                .map((ans) => ans.trim())
                .filter(
                  (ans) => ans !== ""
                );
            } else {
              answerKey = [];
            }
          } else {
            answerKey =
              String(correctAnswerRaw).trim();
          }

          /* ================================
             POINTS
          ================================= */

          const pointsRaw =
            row["Points"] ||
            row["points"] ||
            0;

          const points =
            parseInt(pointsRaw, 10) || 0;

          /* ================================
             REQUIRED
          ================================= */

          const requiredRaw = String(
            row["Required"] ||
              row["required"] ||
              "false"
          )
            .toLowerCase()
            .trim();

          const required =
            requiredRaw === "yes" ||
            requiredRaw === "true";

          return {
            questionText,
            questionType,
            options,
            answer: false,
            answerKey,
            points,
            open: true,
            required,
          };
        });

        /* ================================
           SAVE TO FIREBASE
        ================================= */

        const db = getFirestore(app);

        const formId = uuid();

        const email =
          sessionStorage.getItem("email");

        const formData = {
          formName: file.name.replace(
            /\.[^/.]+$/,
            ""
          ),

          formDescription:
            "Form imported from Excel sheet on " +
            new Date().toLocaleDateString(),

          questions: parsedQuestions,

          saveEmail: email,

          isAnonymous: false,

          activate: false,

          percentage: "50",

          enableTimer: false,

          timerDuration: 0,

          location: null,

          includeCamera: true,

          includeLocation: true,

          showResult: true,
          selectedInstructions: ["read-carefully"],
          customInstructions: "",
        };

        await setDoc(
          doc(db, "forms", formId),
          formData
        );

        setSnack({
          open: true,
          msg: "Form imported successfully!",
          severity: "success",
        });

        // Close popup after successful upload
        setShowInstructionPopup(false);

        setTimeout(() => {
          navigate("/form/" + formId);
        }, 1500);
      } catch (err) {
        console.error(
          "Error parsing Excel file:",
          err
        );

        setSnack({
          open: true,
          msg: "Failed to parse the Excel file. Please ensure column names are correct.",
          severity: "error",
        });
      }
    };

    reader.readAsArrayBuffer(file);
  };

  /* ================================
     UI
  ================================= */

  return (
    <div className="create-form-page">

      <DashboardSidebar
        activePage="Create New Form"
      />

      <main className="create-form-main">

        {/* ================================
            HEADER
        ================================= */}

        <section className="create-form-header">

          <Typography variant="h4">
            Create a New Form
          </Typography>

          <Typography variant="body1">
            Choose how you'd like to create your form
          </Typography>

        </section>

        {/* ================================
            CREATE OPTIONS
        ================================= */}

        <Box className="create-form-options">

          {/* =================================
              CREATE MANUALLY
          ================================== */}

          <Card className="create-form-card">

            <CardContent>

              <Box className="create-form-icon manual-icon">
                <CreateIcon />
              </Box>

              <Typography variant="h6">
                Create Manually
              </Typography>

              <Typography variant="body2">
                Build your form from scratch with our
                intuitive form builder. Add questions,
                customize settings, and design your
                perfect form.
              </Typography>

            </CardContent>

            <CardActions>

              <Button
                onClick={handleCreateManually}
                variant="contained"
              >
                Start Creating
              </Button>

            </CardActions>

          </Card>

          {/* =================================
              UPLOAD EXCEL
          ================================== */}

          <Card className="create-form-card">

            <CardContent>

              <Box className="create-form-icon excel-icon">
                <CloudUploadIcon />
              </Box>

              <Typography variant="h6">
                Upload Excel
              </Typography>

              <Typography variant="body2">
                Import questions from an Excel file.
                Quickly bulk-create forms with your
                existing question data.
              </Typography>

            </CardContent>

            <CardActions>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleExcelUpload}
                style={{
                  display: "none",
                }}
              />

              {/* 
                IMPORTANT:
                Clicking this opens the instructions popup.
                The popup is NOT open by default.
              */}
              <Button
                onClick={() =>
                  setShowInstructionPopup(true)
                }
                variant="contained"
                className="excel-button"
              >
                Upload File
              </Button>

            </CardActions>

          </Card>

        </Box>

        {/* =================================
            EXCEL INSTRUCTIONS POPUP
        ================================== */}

        <Dialog
          open={showInstructionPopup}
          onClose={() =>
            setShowInstructionPopup(false)
          }
          maxWidth="md"
          fullWidth
        >

          <DialogTitle className="excel-dialog-title">
            Excel Import Format Instructions
          </DialogTitle>

          <DialogContent className="excel-dialog-content">

            <Typography
              variant="body1"
              gutterBottom
              className="excel-dialog-description"
            >
              To successfully import questions, your
              Excel sheet must contain specific headers.
              Here is a guide on how to structure your
              spreadsheet:
            </Typography>

            <TableContainer
              component={Paper}
              elevation={0}
              className="excel-table-container"
            >

              <Table size="small">

                <TableHead>

                  <TableRow>

                    <TableCell>
                      Column Header
                    </TableCell>

                    <TableCell>
                      Status
                    </TableCell>

                    <TableCell>
                      Description / Values
                    </TableCell>

                    <TableCell>
                      Example
                    </TableCell>

                  </TableRow>

                </TableHead>

                <TableBody>

                  <TableRow>

                    <TableCell>
                      <strong>
                        QuestionText
                      </strong>
                    </TableCell>

                    <TableCell className="required-cell">
                      Required
                    </TableCell>

                    <TableCell>
                      The text of your question.
                      (Aliases: <i>Question</i>,
                      <i> question</i>)
                    </TableCell>

                    <TableCell>
                      What is 2 + 2?
                    </TableCell>

                  </TableRow>

                  <TableRow>

                    <TableCell>
                      <strong>
                        QuestionType
                      </strong>
                    </TableCell>

                    <TableCell>
                      Optional
                    </TableCell>

                    <TableCell>
                      Type of question:
                      <b> radio</b> (single choice),
                      <b> checkbox</b> (multiple
                      choices), or
                      <b> text</b> (short text box).
                    </TableCell>

                    <TableCell>
                      radio
                    </TableCell>

                  </TableRow>

                  <TableRow>

                    <TableCell>
                      <strong>
                        Option1 - Option5
                      </strong>
                    </TableCell>

                    <TableCell>
                      Optional
                    </TableCell>

                    <TableCell>
                      Columns for option choices.
                      Add columns such as
                      Option1, Option2, Option3...
                    </TableCell>

                    <TableCell>
                      3, 4, 5
                    </TableCell>

                  </TableRow>

                  <TableRow>

                    <TableCell>
                      <strong>
                        CorrectAnswer
                      </strong>
                    </TableCell>

                    <TableCell>
                      Optional
                    </TableCell>

                    <TableCell>
                      Correct option text. For
                      multiple correct answers
                      (checkboxes), separate them
                      with semicolons.
                    </TableCell>

                    <TableCell>
                      4
                    </TableCell>

                  </TableRow>

                  <TableRow>

                    <TableCell>
                      <strong>
                        Points
                      </strong>
                    </TableCell>

                    <TableCell>
                      Optional
                    </TableCell>

                    <TableCell>
                      Points awarded for a correct
                      answer. Defaults to 0.
                    </TableCell>

                    <TableCell>
                      5
                    </TableCell>

                  </TableRow>

                  <TableRow>

                    <TableCell>
                      <strong>
                        Required
                      </strong>
                    </TableCell>

                    <TableCell>
                      Optional
                    </TableCell>

                    <TableCell>
                      Mark as <b>Yes</b> or
                      <b> No</b>. Defaults to No.
                    </TableCell>

                    <TableCell>
                      Yes
                    </TableCell>

                  </TableRow>

                </TableBody>

              </Table>

            </TableContainer>

            <Typography
              variant="body2"
              className="excel-dialog-note"
            >
              Note: You can use .xlsx, .xls, or .csv
              formats. The first sheet of the workbook
              will be processed.
            </Typography>

          </DialogContent>

          {/* =================================
              POPUP ACTIONS
          ================================== */}

          <DialogActions className="excel-dialog-actions">

            <Button
              onClick={downloadTemplate}
              variant="outlined"
              className="download-template-button"
              startIcon={<FileDownloadIcon />}
            >
              Download Template
            </Button>

            <Box>

              <Button
                onClick={() =>
                  setShowInstructionPopup(false)
                }
                className="cancel-button"
              >
                Cancel
              </Button>

              <Button
                onClick={() => {
                  setShowInstructionPopup(false);

                  // Open file picker
                  fileInputRef.current?.click();
                }}
                variant="contained"
                className="select-file-button"
              >
                Select Excel File
              </Button>

            </Box>

          </DialogActions>

        </Dialog>

        {/* =================================
            SNACKBAR
        ================================== */}

        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={() =>
            setSnack({
              ...snack,
              open: false,
            })
          }
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >

          <Alert
            severity={snack.severity}
            sx={{
              width: "100%",
            }}
          >
            {snack.msg}
          </Alert>

        </Snackbar>

      </main>

    </div>
  );
}

export default CreateForm;